import React, { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Check, X, EyeOff, ShieldAlert } from "lucide-react";
import { cn } from "@/utils/stylesUtils";

const ParticipationTable = ({
  practiced,
  didNotPractice,
  nonConsented = [],
}) => {
  const [activeTab, setActiveTab] = useState("practiced");
  const [visibleCount, setVisibleCount] = useState(50);

  const dataToShow = (() => {
    if (activeTab === "practiced") return practiced;
    if (activeTab === "didNotPractice") return didNotPractice;
    if (activeTab === "hidden") return nonConsented;
    return [];
  })();

  const visibleStudents = dataToShow.slice(0, visibleCount);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setVisibleCount((prev) => prev + 50);
    }
  };

  const renderLastPractice = (records) => {
    if (!records.length) return "--";
    const last = records.reduce((latest, curr) =>
      new Date(curr.date) > new Date(latest.date) ? curr : latest
    );
    return formatDistanceToNow(new Date(last.date), {
      addSuffix: true,
    }).replace(/^about /, "");
  };

  const renderNumPractices = (records) => records.length;

  const tabs = [
    {
      key: "practiced",
      label: `Practiced (${practiced.length})`,
      icon: Check,
      color: "green",
    },
    {
      key: "didNotPractice",
      label: `Did Not Practice (${didNotPractice.length})`,
      icon: X,
      color: "red",
    },
    {
      key: "hidden",
      label: `Hidden (${nonConsented.length})`,
      icon: EyeOff,
      color: "gray",
    },
  ];

  return (
    <div className="w-full relative">
      {/* Tabs */}
      <div className="flex gap-6 text-base border-b-2 border-[#2D3748]/5 mb-3">
        {tabs.map(({ key, label, icon: Icon, color }) => (
          <div
            key={key}
            className={cn(
              "cursor-pointer flex gap-1 items-center transition-all",
              activeTab === key
                ? "font-bold text-black border-b-2 border-[#2D3748]"
                : "text-[#2D374840]"
            )}
            onClick={() => {
              setActiveTab(key);
              setVisibleCount(50);
            }}
          >
            <Icon
              className={cn(
                "w-4 h-4 transition-colors",
                activeTab === key ? `text-${color}-600` : "text-gray-400"
              )}
            />
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Desktop table header */}
      <div
        className={cn(
          "hidden sm:grid text-left px-3 py-1 bg-white sticky top-0 z-10 text-[#000000]/50 text-base font-bold border-b border-[#F4F5F6]",
          activeTab === "practiced" && "grid-cols-[2fr_1.2fr_1fr]",
          activeTab === "didNotPractice" && "grid-cols-2",
          activeTab === "hidden" && "grid-cols-1"
        )}
      >
        {activeTab === "hidden" ? (
          <div className="col-span-full -mx-3 -my-1 px-6 py-5 bg-gradient-to-r from-amber-50/80 via-amber-50/50 to-transparent border-l-4 border-amber-500 relative overflow-hidden group animate-in fade-in slide-in-from-left-2 duration-500">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="flex items-start gap-3 relative">
              <ShieldAlert className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0 animate-in zoom-in duration-300 delay-150" />
              <p className="text-amber-900/90 font-semibold text-base leading-relaxed">
                These students have chosen not to share their data
                <br />
                in their privacy settings.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div>STUDENT</div>
            {activeTab === "practiced" && <div>LAST PRACTICE</div>}
            <div className="text-right"># OF PRACTICES</div>
          </>
        )}
      </div>

      {/* MOBILE sticky banner (above scroll) */}
      {activeTab === "hidden" && (
        <div className="block sm:hidden sticky top-0 z-20">
          <div className="px-3 py-4 bg-amber-50 border-l-4 border-amber-500 shadow-sm">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <p className="text-amber-900/90 font-semibold text-sm leading-relaxed">
                These students have chosen not to share their data in their
                privacy settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile list */}
      <div
        className="relative block sm:hidden max-h-[250px] overflow-y-auto"
        onScroll={handleScroll}
      >
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white to-transparent z-10" />

        <div className="divide-y divide-[#F4F5F6] animate-in fade-in duration-300">
          {visibleStudents.length === 0 ? (
            <p className="text-gray-500 italic mt-3 px-3">No data available.</p>
          ) : (
            visibleStudents.map((student, i) => (
              <div
                key={i}
                className="px-3 py-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
                style={{ animationDelay: `${i * 15}ms` }}
              >
                {activeTab === "hidden" ? (
                  <span className="font-mono text-sm">
                    {student.displayName || student.email}
                  </span>
                ) : (
                  <>
                    <div className="text-base font-bold">
                      {student.studentName}
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      {activeTab === "practiced" && (
                        <div className="flex items-baseline gap-1.5">
                          <span className="uppercase text-[#000000]/50 font-bold text-xs">
                            LAST PRACTICE
                          </span>
                          <span className="text-base">
                            {renderLastPractice(student.records)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-baseline gap-1.5">
                        <span className="uppercase text-[#000000]/50 font-bold text-xs">
                          # OF PRACTICES
                        </span>
                        <span className="text-right text-base">
                          {activeTab === "practiced"
                            ? renderNumPractices(student.records)
                            : 0}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent z-10" />
      </div>

      {/* Desktop list */}
      <div
        className="hidden sm:block max-h-[250px] overflow-y-auto relative"
        onScroll={handleScroll}
      >
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white to-transparent z-10" />

        <div className="animate-in fade-in duration-300">
          {visibleStudents.length === 0 ? (
            <p className="text-gray-500 italic mt-3 px-3">No data available.</p>
          ) : (
            visibleStudents.map((student, i) => (
              <div
                key={i}
                className={cn(
                  "grid items-center text-left px-3 py-3 border-b border-[#F4F5F6] text-xs sm:text-[14px] animate-in fade-in slide-in-from-bottom-2 duration-300",
                  activeTab === "practiced"
                    ? "grid-cols-[2fr_1.2fr_1fr]"
                    : "grid-cols-2"
                )}
                style={{ animationDelay: `${i * 12}ms` }}
              >
                {activeTab === "hidden" ? (
                  <span className="font-mono">
                    {student.displayName || student.email}
                  </span>
                ) : (
                  <>
                    <div>{student.studentName}</div>
                    {activeTab === "practiced" && (
                      <div>{renderLastPractice(student.records)}</div>
                    )}
                    <div className="text-right">
                      {activeTab === "practiced"
                        ? renderNumPractices(student.records)
                        : 0}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent z-10" />
      </div>
    </div>
  );
};

export default ParticipationTable;
